import React, { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWhoami } from '@cotera/client/app/stores/org';
import {
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { CocoEvent, useAnalytics, useTracking } from '@cotera/client/app/etc';
import {
  type FilterOption,
  type SortOption,
  type ItemAction,
} from '../../resources/list/resource-list-view';
import {
  ResourceMenuItem,
  shareMoveArchiveItems,
} from '../../resources/list/resource-item-actions';
import { useFavoriteMenuItem } from '../../resources/favorites';
import {
  ChatHistoryListPageModel,
  useChatHistoryListPageModel,
  type ConversationListItem,
} from '../models/chat-history-list-page.model';
import {
  AGENT_CHAT_COLUMNS,
  CHAT_COLUMNS,
  ChatListView,
  getDisplayTitle,
} from '../components/chat-list-view';
import {
  CHAT_TRIGGER_GROUPS,
  CHAT_TRIGGER_GROUP_LABELS,
  type ChatTriggerGroup,
} from '../utils/chat-trigger-source';
import { ResourceType } from '../../resources/selection/resource-selection.model';
import { MoveToFolderAction } from '../../resources/actions/move-to-folder.action';
import { ShareResourceLinkAction } from '../../resources/actions/share-resource-link.action';
import { ArchiveResourceAction } from '../../resources/actions/archive-resource.action';
import {
  ListPageShell,
  useListPageModelFactory,
} from '../../resources/list-page-shell';
import { useResourceListSelection } from '../../resources/selection/use-resource-list-selection';
import { useFolderAncestors } from '../../folder/use-folder-ancestors';
import { buildFolderTrailCrumbs } from '../../folder/folder-breadcrumbs';
import type { BreadcrumbItem } from '../../resources/resource-page-shell';

/**
 * Trail for a folder-scoped chat history (`/chats?folderId=…`), which is where
 * a folder's collapsed "Chats" row opens. `undefined` for the unscoped stream —
 * that one is a top-level destination with nothing above it.
 *
 * Without this the folder's chats rendered under a bare header: the trail you
 * had been following down the Library ended, and the page named neither the
 * folder you came from nor offered a way back to it.
 */
function useFolderChatsBreadcrumbs(
  folderId: string | null
): BreadcrumbItem[] | 'loading' | undefined {
  const org = useWhoami((s) => s.org);
  const { ancestors, folder, isLoading } = useFolderAncestors(folderId);

  return useMemo((): BreadcrumbItem[] | 'loading' | undefined => {
    if (folderId === null) {
      return undefined;
    }
    if (isLoading) {
      return 'loading';
    }
    return buildFolderTrailCrumbs({
      orgSlug: org.slug,
      // `null` for a folder we can't read; the builder shortens the trail to the
      // Library root rather than dropping it.
      folder:
        folder === null
          ? null
          : { id: folderId, name: folder.name, scope: folder.scope },
      ancestors,
      leaf: 'Chats',
    });
  }, [folderId, isLoading, folder, ancestors, org.slug]);
}

const ChatHistoryListContent: React.FC = () => {
  const org = useWhoami((s) => s.org);
  const [searchParams] = useSearchParams();
  const listPageModel = useChatHistoryListPageModel();
  const moveToFolderAction = useAction(MoveToFolderAction);
  const shareAction = useAction(ShareResourceLinkAction);
  const archiveResourceAction = useAction(ArchiveResourceAction);
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { handleOpenActionMenu, handleFocusItems } = useResourceListSelection();

  useTracking(CocoEvent.ChatHistoryViewed, { orgId: org.id });

  React.useEffect(() => {
    listPageModel.syncFromUrl(searchParams);
  }, [listPageModel, searchParams]);

  const listParams = useWatchableValue(listPageModel.listParamsWatchable);
  const {
    page,
    limit,
    search,
    sort,
    sortDir,
    unreadOnly,
    triggerGroups,
    folderId,
  } = listParams;
  const chatsData = useWatchableValue(listPageModel.chatsDataWatchable);

  // A folder-scoped list is fetched through `getFolderItems`, whose chat items
  // carry no `lastMessage`/`firstMessage` (see `chatItemSchema`: "Chat list
  // includes lastMessage; folder items omit it"). So there is no snippet to show
  // here — asking for one printed "No messages" under chats that had a whole
  // conversation in them. Rows in a folder carry their title alone: the folder
  // label they used to show is what the breadcrumb now says.
  const isFolderScoped = folderId !== undefined && folderId !== '';

  const conversations = useMemo(() => chatsData?.items ?? [], [chatsData]);
  const total = chatsData?.total ?? 0;
  const hasMore = total > limit * page;
  const chatsStatus = chatsData === null ? 'loading' : 'ready';

  const handleLoadPage = useCallback(
    (p: number, take: number) => {
      listPageModel.loadPage(p, take);
    },
    [listPageModel]
  );

  const handleNewChat = useCallback(() => {
    navigate(`/${org.slug}`);
  }, [navigate, org.slug]);

  const handleClickConversation = useCallback(
    (conversation: ConversationListItem) => {
      analytics.track(CocoEvent.ChatOpenedFromHistory, {
        conversationId: conversation.id,
        source: 'history-page',
        orgId: org.id,
      });
      navigate(`/${org.slug}/chats/${conversation.id}`);
    },
    [analytics, navigate, org.id, org.slug]
  );

  const handleSearchChange = useCallback(
    (value: string) => listPageModel.setSearch(value),
    [listPageModel]
  );
  const handleSortChange = useCallback(
    (key: string, direction: 'asc' | 'desc') =>
      listPageModel.setSort(key, direction),
    [listPageModel]
  );
  const handleUnreadOnlyChange = useCallback(
    (checked: boolean) => listPageModel.setUnreadOnly(checked),
    [listPageModel]
  );

  const toggleTriggerGroup = useCallback(
    (group: ChatTriggerGroup, checked: boolean) => {
      // Keep the declared order so the URL value is stable regardless of the
      // order the boxes were ticked in.
      const next = checked
        ? CHAT_TRIGGER_GROUPS.filter(
            (candidate) =>
              candidate === group || triggerGroups.includes(candidate)
          )
        : triggerGroups.filter((candidate) => candidate !== group);
      // Backstop only: the menu disables the last selected source, so this is
      // unreachable from the UI. A hand-edited `?triggers=` can still get here,
      // and listing nothing is worse than ignoring the change.
      if (next.length === 0) {
        return;
      }
      listPageModel.setTriggerGroups(next);
    },
    [listPageModel, triggerGroups]
  );

  const filterOptions: FilterOption[] = useMemo(() => {
    // The sources are a set that has to keep one member — with none selected the
    // list would be empty, which is never what the click meant. Rather than
    // accept the click and silently ignore it (which read as a broken checkbox,
    // since "Chats" appeared stuck on until you happened to tick a second
    // source), the last one standing goes inert and says why.
    const onlySelectedGroup =
      triggerGroups.length === 1 ? triggerGroups[0] : undefined;
    return [
      // Sources first: they decide what the list is *of*, where "Unread only"
      // narrows whatever that turns out to be.
      ...CHAT_TRIGGER_GROUPS.map((group) => {
        const isLocked = group === onlySelectedGroup;
        return {
          label: CHAT_TRIGGER_GROUP_LABELS[group],
          key: `trigger-${group}`,
          section: 'Show',
          checked: triggerGroups.includes(group),
          disabled: isLocked,
          hint: isLocked ? 'Select another source first' : undefined,
          onChange: (checked: boolean) => toggleTriggerGroup(group, checked),
        };
      }),
      {
        label: 'Unread only',
        key: 'unread',
        section: 'Filter',
        checked: unreadOnly,
        onChange: handleUnreadOnlyChange,
      },
    ];
  }, [unreadOnly, handleUnreadOnlyChange, triggerGroups, toggleTriggerGroup]);

  // The trigger column only earns its space once non-chat triggers can appear.
  const showTriggerColumn = triggerGroups.some((group) => group !== 'chat');

  const handleOpenMenu = useCallback(
    (conversation: ConversationListItem) =>
      handleOpenActionMenu({
        id: conversation.id,
        name: conversation.title ?? 'Untitled chat',
        resourceType: ResourceType.Chat,
      }),
    [handleOpenActionMenu]
  );

  const handleFocusConversations = useCallback(
    (focusedConversations: ConversationListItem[]) =>
      handleFocusItems(
        focusedConversations.map((conversation) => ({
          id: conversation.id,
          name: conversation.title ?? 'Untitled chat',
          resourceType: ResourceType.Chat,
        }))
      ),
    [handleFocusItems]
  );

  const favoriteMenuItem = useFavoriteMenuItem();

  const getConversationActions = useCallback(
    (conversation: ConversationListItem): ItemAction[] => [
      favoriteMenuItem(ResourceType.Chat, conversation.id),
      ResourceMenuItem.open(() => {
        analytics.track(CocoEvent.ChatOpenedFromHistory, {
          conversationId: conversation.id,
          source: 'history-page-menu',
          orgId: org.id,
        });
        navigate(`/${org.slug}/chats/${conversation.id}`);
      }),
      ...shareMoveArchiveItems({
        onShare: () => {
          handleOpenMenu(conversation);
          void shareAction.execute({});
        },
        onMoveToFolder: () => {
          handleOpenMenu(conversation);
          void moveToFolderAction.execute({});
        },
        onArchive: () => {
          handleOpenMenu(conversation);
          void archiveResourceAction.execute({});
        },
      }),
    ],
    [
      analytics,
      favoriteMenuItem,
      navigate,
      org.id,
      org.slug,
      shareAction,
      moveToFolderAction,
      archiveResourceAction,
      handleOpenMenu,
    ]
  );

  const sortOptions: SortOption<ConversationListItem>[] = useMemo(
    () => [
      {
        label: 'Title',
        key: 'title',
        compareFn: (a, b) =>
          getDisplayTitle(a.title, {
            firstMessage: a.firstMessage,
            lastMessage: a.lastMessage,
          }).localeCompare(
            getDisplayTitle(b.title, {
              firstMessage: b.firstMessage,
              lastMessage: b.lastMessage,
            })
          ),
      },
      {
        label: 'Date',
        key: 'date',
        compareFn: (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      },
    ],
    []
  );

  return (
    <ChatListView
      headerSize="compact"
      containerClassName="px-0"
      items={conversations}
      status={chatsStatus}
      // The server total, not the loaded slice: with a page size of 50 a
      // 300-chat list reported "50 chats" until you scrolled.
      totalCount={total}
      showPreview={!isFolderScoped}
      // The full stream keeps the folder label — there it tells you where a chat
      // actually lives, which is the one thing its row can't otherwise say.
      hideFolderName={isFolderScoped}
      columnLayout={showTriggerColumn ? 'agent' : 'default'}
      columns={showTriggerColumn ? AGENT_CHAT_COLUMNS : CHAT_COLUMNS}
      onClickConversation={handleClickConversation}
      searchQuery={search}
      onSearchQueryChange={handleSearchChange}
      sortOptions={sortOptions}
      activeSort={sort}
      sortDirection={sortDir}
      onSortChange={handleSortChange}
      filterOptions={filterOptions}
      itemActions={getConversationActions}
      onFocus={handleFocusConversations}
      onOpenActionMenu={handleOpenMenu}
      primaryAction={{
        label: 'New chat',
        onClick: handleNewChat,
      }}
      hasMore={hasMore}
      loadPage={handleLoadPage}
      page={page}
      take={limit}
    />
  );
};

export const ChatHistoryPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const folderId = searchParams.get('folderId');
  const breadcrumbs = useFolderChatsBreadcrumbs(
    folderId === null || folderId === '' ? null : folderId
  );
  const { createModels, deps } = useListPageModelFactory({
    createModel: ({
      client,
      urlAdapter,
      queryClient,
      orgId,
      watchablesFactory,
    }) =>
      new ChatHistoryListPageModel({
        orgId,
        queryClient,
        watchablesFactory,
        urlAdapter,
        initialSearchParams: new URLSearchParams(
          typeof window !== 'undefined' ? window.location.search : ''
        ),
        client,
      }),
  });

  return (
    /*
     * The standard wide-resource-list frame, matching the Library and
     * Automations: one 1640px cap so very wide displays don't stretch rows to an
     * unreadable line length, one set of responsive gutters, one bottom margin.
     * The gutters live out here rather than inside `ResourceListView` — whose
     * default `px-8` the `containerClassName` below drops — so the two never
     * stack.
     */
    <ListPageShell
      createModels={createModels}
      deps={deps}
      contentClassName="max-w-[1640px] px-5 pb-16 pt-1 sm:px-8 sm:pt-5 lg:px-12"
      breadcrumbs={breadcrumbs}
    >
      <ChatHistoryListContent />
    </ListPageShell>
  );
};
